/**
 * @file    cprPlugin.tsx
 * @scope   CPR
 * @summary Cross-Program Rollup registration in the drawer engine host.
 * @consumes CprContext, CprRoutes, useCprTabCleanup, useCprTrendSync
 * @produces cprPlugin
 * @sideEffects Provider mirrors the CPR slice into `engineSlices[CPR_CHART_TYPE]`.
 */
import React, { useCallback } from 'react';
import { useStore } from 'store/useGlobalStore';
import { CprProvider } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/state/CprContext';
import { CprSlice } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/model';
import { CPR_CHART_TYPE } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/constants';
import CprRoutes from '../../sections/CprRoutes';
import { useCprTabCleanup } from '../../hooks/useCprTabCleanup';
import { useCprTrendSync } from '../../hooks/useCprTrendSync';
import { CprNavigationProvider } from './CprNavigationContext';
import { EngineSubtypePlugin } from '../types';

const CprPluginProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const setState = useStore((state) => state.setState);
  const engineSlices = useStore((state) => state.engineSlices);

  // WHY: keyed by subtype id so the preview (outside this provider) can find it.
  const handleStateChange = useCallback(
    (slice: CprSlice) => setState({ engineSlices: { ...engineSlices, [CPR_CHART_TYPE]: slice } }),
    [setState, engineSlices]
  );

  return (
    <CprProvider onStateChange={handleStateChange}>
      <CprNavigationProvider>{children}</CprNavigationProvider>
    </CprProvider>
  );
};

export const cprPlugin: EngineSubtypePlugin = {
  id: CPR_CHART_TYPE,
  Provider: CprPluginProvider,
  Routes: CprRoutes,
  useTabCleanup: useCprTabCleanup,
  useTrendSync: useCprTrendSync,
};
